import { useNavigate } from 'react-router-dom';
import { ArrowRight, Download, FileCheck, FileText, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Skeleton } from '@/shared/ui/Skeleton';
import { formatDate } from '@/shared/utils/format';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { EquipmentItem } from '../api/safetyApi';
import { useSafetyOverview } from '../hooks';
import {
  AssignmentStatusPill,
  AttestationFooter,
  EquipmentStatusPill,
  PnTag,
  SafetyLoadError,
  SeverityDot,
} from '../components/SafetyBits';
import styles from './SafetyOverviewPage.module.css';

const CADENCE_GROUPS: { key: string; label: string }[] = [
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'state', label: 'State cycle' },
  { key: 'per_load', label: 'Per load' },
];

function routineSummary(items: EquipmentItem[]): { pill: EquipmentItem['status']; note: string } {
  const worst =
    items.find((i) => i.status === 'action_required') ??
    items.find((i) => i.status === 'overdue') ??
    items.find((i) => i.status === 'due_soon') ??
    items[0];
  const ok = items.filter((i) => i.status === 'ok' || i.status === 'event_driven').length;
  return { pill: worst.status, note: `${ok} of ${items.length} current` };
}

export function SafetyOverviewPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch, locationId } = useSafetyOverview();

  if (isLoading || !locationId) {
    return (
      <div className={patterns.page}>
        <Skeleton height={120} />
        <Skeleton height={280} />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={patterns.page}>
        <PageHeader
          title="Safety & Training"
          subtitle="Deliver, quiz, and document the safety training your team and equipment require."
        />
        <SafetyLoadError onRetry={() => refetch()} />
      </div>
    );
  }

  if (!data.onboarded) {
    return (
      <div className={patterns.page}>
        <PageHeader
          title="Safety & Training"
          subtitle="Deliver, quiz, and document the safety training your team and equipment require — and keep an inspection-ready record."
        />
        <EmptyState
          icon={<ShieldCheck size={28} aria-hidden />}
          title="One bundled annual training. One practice-wide date."
          description="We draft a starter plan from your team roster and the standard California equipment set — you confirm it in about 2 minutes. OSHA and HIPAA training, equipment logs, and an inspection-ready binder, without the spreadsheet."
          actions={
            <div className={styles.zeroActions}>
              <Button variant="primary" onClick={() => navigate('/safety/setup')}>
                Start setup
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  const { rollup, attention, staff, equipment, ecp, recordCount } = data;
  const ringCircumference = 2 * Math.PI * 26;

  const groups = CADENCE_GROUPS.map((g) => ({
    ...g,
    items: equipment.filter((e) => e.cadence === g.key),
  })).filter((g) => g.items.length > 0);

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Safety & Training"
        subtitle="Deliver, quiz, and document the safety training your team and equipment require. We deliver and record training; your practice remains responsible for compliance."
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download size={14} aria-hidden />}
            onClick={() => navigate('/safety/records/binder')}
          >
            Export inspection binder
          </Button>
        }
      />

      {/* Hero — completion ring */}
      <div className={`${patterns.panel} ${styles.hero}`}>
        <div className={styles.ringWrap}>
          <svg viewBox="0 0 64 64" className={styles.ring} role="img" aria-label="Completion">
            <circle className={styles.ringTrack} cx="32" cy="32" r="26" />
            <circle
              className={styles.ringValue}
              cx="32"
              cy="32"
              r="26"
              strokeDasharray={`${(rollup.pct / 100) * ringCircumference} ${ringCircumference}`}
            />
          </svg>
          <span className={styles.ringLabel}>{rollup.pct}%</span>
        </div>
        <div className={styles.heroText}>
          <h2 className={styles.heroTitle}>Annual Safety &amp; Compliance Training</h2>
          <p className={styles.heroSub}>
            {rollup.complete} of {rollup.total} complete &amp; e-signed · Next cycle:{' '}
            {data.nextCycleLabel}
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => navigate('/safety/training')}>
          Staff training
        </Button>
      </div>

      {/* Needs attention */}
      <section>
        <div className={patterns.sectionHeader}>
          <span className={patterns.sectionTitle}>Needs attention</span>
          <span className={patterns.sectionFootnote}>
            Due and overdue items, computed from each requirement’s cadence
          </span>
        </div>
        <div className={patterns.panel}>
          {attention.length === 0 ? (
            <p className={styles.allClear}>Nothing needs attention — everything is current.</p>
          ) : (
            <div className={styles.attnList}>
              {attention.map((item, i) => (
                <div key={i} className={styles.attnRow}>
                  <SeverityDot severity={item.severity} />
                  <div className={styles.attnBody}>
                    <div className={styles.attnTitle}>
                      <span>{item.title}</span>
                      <PnTag action={item.pnAction} />
                    </div>
                    <div className={styles.attnDetail}>{item.detail}</div>
                  </div>
                  <button className={styles.attnLink} onClick={() => navigate(item.href)}>
                    Open <ArrowRight size={12} aria-hidden />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Team status — one status per person */}
      <section>
        <div className={patterns.sectionHeader}>
          <span className={patterns.sectionTitle}>Team</span>
          <span className={patterns.sectionFootnote}>One bundled course · one status each</span>
        </div>
        <div className={patterns.gridTable}>
          <div className={`${patterns.gridTableHeader} ${styles.teamGrid}`}>
            <span>Team member</span>
            <span>Status</span>
            <span>Due</span>
            <span>Completed</span>
            <span />
          </div>
          {staff.map((member) => (
            <div key={member.id} className={`${patterns.gridTableRow} ${styles.teamGrid}`}>
              <div>
                <div className={patterns.cellName}>{member.name}</div>
                <div className={patterns.cellSmMuted}>{member.roleLabel}</div>
              </div>
              <span>
                {member.assignment ? (
                  <AssignmentStatusPill status={member.assignment.status} />
                ) : (
                  '—'
                )}
              </span>
              <span className={patterns.cellMuted}>
                {member.assignment ? formatDate(member.assignment.dueDate) : '—'}
              </span>
              <span className={patterns.cellMuted}>
                {member.assignment?.completedAt
                  ? `${formatDate(member.assignment.completedAt)} · ${Math.round(
                      (member.assignment.quizScore ?? 0) * 100,
                    )}%`
                  : '—'}
              </span>
              <span className={styles.rowAction}>
                {member.assignment && member.assignment.status !== 'complete' && (
                  <button
                    className={styles.attnLink}
                    onClick={() => navigate(`/safety/training/${member.assignment?.id}/take`)}
                  >
                    Take training <ArrowRight size={12} aria-hidden />
                  </button>
                )}
              </span>
            </div>
          ))}
          {staff.length === 0 && (
            <div className={patterns.gridTableEmpty}>
              No team members yet — add them in Staff Training.
            </div>
          )}
        </div>
      </section>

      {/* Equipment routines — grouped by cadence, one status each */}
      {groups.length > 0 && (
        <section>
          <div className={patterns.sectionHeader}>
            <span className={patterns.sectionTitle}>Equipment routines</span>
            <span className={patterns.sectionFootnote}>
              Cadence is set by regulation — grouped so a week has one checklist
            </span>
          </div>
          <div className={styles.routineGrid}>
            {groups.map((group) => {
              const summary = routineSummary(group.items);
              return (
                <button
                  key={group.key}
                  className={`${patterns.panelCompact} ${styles.routineCard}`}
                  onClick={() => navigate('/safety/equipment')}
                >
                  <div className={styles.routineTop}>
                    <span className={styles.routineLabel}>{group.label}</span>
                    <EquipmentStatusPill status={summary.pill} />
                  </div>
                  <div className={styles.routineNote}>
                    {group.items.length} {group.items.length === 1 ? 'check' : 'checks'} ·{' '}
                    {summary.note}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Stats row */}
      <div className={patterns.statGrid}>
        <div className={`${patterns.panelCompact} ${styles.stat}`}>
          <FileCheck size={16} aria-hidden className={styles.statIcon} />
          <div className={styles.statValue}>{recordCount}</div>
          <div className={styles.statLabel}>completion records on file (3-yr retention)</div>
        </div>
        <div className={`${patterns.panelCompact} ${styles.stat}`}>
          <FileText size={16} aria-hidden className={styles.statIcon} />
          <div className={styles.statTitle}>Exposure Control Plan</div>
          <div className={styles.statLabel}>
            {ecp?.reviewedAt
              ? `Reviewed ${formatDate(ecp.reviewedAt)} · next review ${
                  ecp.nextReview ? formatDate(ecp.nextReview) : '—'
                }`
              : 'Generated at setup — review it in Records'}
          </div>
        </div>
        <div className={`${patterns.panelCompact} ${styles.stat}`}>
          <Download size={16} aria-hidden className={styles.statIcon} />
          <div className={styles.statTitle}>Inspection binder</div>
          <div className={styles.statLabel}>
            One document: records, logs, ECP, requirements table.
          </div>
        </div>
      </div>

      <AttestationFooter text={data.attestation} />
    </div>
  );
}
